/**
 * concierge/models.ts — types for Engine #10, the Roamer AI Concierge™.
 *
 * Not a generic chatbot: an intelligent travel + remote-work assistant that
 * reasons over context gathered from the other engines. The concierge NEVER
 * fabricates local information — every answer is assembled from the supplied
 * context snapshot, provenance-labelled (verified vs community vs predicted
 * vs suggested), and explained.
 */

// ─── Intents (supported tasks) ─────────────────────────────────────────────────

export type ConciergeIntent =
  | 'recommendNextCity'
  | 'compareCities'
  | 'quieterNeighborhood'
  | 'bestWorkspaceToday'
  | 'reduceExpenses'
  | 'explainScore'
  | 'recommendHousing'
  | 'planTomorrow'
  | 'cafesWithWifi'
  | 'saferNeighborhoods'
  | 'cheaperTransportation'
  | 'localCustoms'
  | 'weekendActivities'
  | 'weekItinerary'
  | 'optimizeBudget'
  | 'visaRenewal'
  | 'dailySummary'
  | 'unknown';

// ─── Context (gathered from the engines — never fabricated) ───────────────────

/** How trustworthy a context item is — surfaced to the user in plain language. */
export type Provenance = 'verified' | 'community' | 'predicted' | 'suggested';

export interface CityContext {
  id: string;
  name: string;
  costOfLivingUsd?: number;
  internetMbps?: number;
  safetyScore?: number;
  tags: string[];
  verified: boolean;
  communityRating?: number;
  communityReviewCount?: number;
}

export interface NeighborhoodContext {
  name: string;
  city?: string;
  monthlyCostUsd?: number;
  /** 0–100, lower is quieter. */
  noiseLevel?: number;
  safetyScore?: number;
  tags: string[];
  verified: boolean;
  communitySummary?: string;
}

export interface WorkspaceContext {
  name: string;
  kind: 'coworking' | 'cafe';
  internetMbps?: number;
  /** Expected occupancy today, 0–100. */
  occupancyPct?: number;
  travelMinutes?: number;
  verified: boolean;
  communityRating?: number;
}

export interface HousingContext {
  title: string;
  monthlyCostUsd?: number;
  internetMbps?: number;
  neighborhood?: string;
  verified: boolean;
}

export interface TransportContext {
  mode: string;
  monthlyCostUsd?: number;
  note?: string;
  verified: boolean;
}

export interface ExpenseContext {
  category: string;
  monthlyUsd: number;
}

export interface CustomContext {
  title: string;
  text: string;
  verified: boolean;
}

export interface EventContext {
  title: string;
  startsAt: number;
  category: string;
  priceUsd?: number;
  communityRecommended?: boolean;
}

export interface PredictionContext {
  title: string;
  detail: string;
  confidence: number;
}

export interface ScoreContext {
  score: number;
  components: { label: string; value: number; detail: string }[];
}

/**
 * Everything the concierge may reason over, gathered by an injectable
 * ContextGatherer from Core/Location/Intelligence/Mission/Fusion/Learning/
 * Community/Predictive/Knowledge/Score. All slices optional — missing data
 * makes the concierge say so, never invent.
 */
export interface ConciergeContext {
  currentBase?: { locationId: string; city: string; country?: string };
  /** Learned preferences from the Learning Engine (plain key → value). */
  preferences?: Record<string, string | number | boolean>;
  missionHistory?: { completedCount: number; recentTitles: string[]; totalEarnedUsd?: number };
  roamerScore?: ScoreContext;
  cities?: CityContext[];
  neighborhoods?: NeighborhoodContext[];
  workspaces?: WorkspaceContext[];
  housing?: HousingContext[];
  transportOptions?: TransportContext[];
  expenses?: ExpenseContext[];
  customs?: CustomContext[];
  events?: EventContext[];
  visa?: { expiresAt?: number; renewalSteps?: string[] };
  predictions?: PredictionContext[];
  /** Plain-English community insights relevant to the current base. */
  communityInsights?: string[];
  /** Already-summarized important updates for today. */
  todayUpdates?: string[];
}

// ─── Responses ─────────────────────────────────────────────────────────────────

/** Explainability is mandatory on every recommendation. */
export interface ConciergeRecommendation {
  title: string;
  detail: string;
  whySelected: string;
  supportingFactors: string[];
  /** 0–100. */
  confidence: number;
  communityInsights: string[];
  /** Alternative options with a one-line reason each. */
  alternatives: { title: string; reason: string }[];
  provenance: Provenance;
}

/** Future platform actions the concierge may propose (routed through Core). */
export type ConciergeActionType =
  | 'createMission'
  | 'saveRecommendation'
  | 'compareCities'
  | 'bookmarkHousing'
  | 'openLocalGuide'
  | 'refreshLocalData'
  | 'addFavorite'
  | 'shareReport'
  | 'generateItinerary'
  | 'prepareNotifications';

export interface ConciergeActionRequest {
  type: ConciergeActionType;
  payload: Record<string, string | number | boolean>;
}

export interface ConciergeResponse {
  intent: ConciergeIntent;
  /** Plain-language reply — never exposes internal engine architecture. */
  reply: string;
  recommendations: ConciergeRecommendation[];
  /** Actions proposed for this turn (dispatched only via requestAction/Core). */
  suggestedActions: ConciergeActionRequest[];
  /**
   * Which context slices informed the answer (personalization audit — kept
   * machine-readable, not user prose).
   */
  contextUsed: string[];
  at: number;
}

// ─── Session memory ────────────────────────────────────────────────────────────

export interface ConciergeTurn {
  role: 'user' | 'concierge';
  text: string;
  intent?: ConciergeIntent;
  at: number;
}

export interface ConciergeSettings {
  /** When false, no session transcript is kept and no learning events emit. */
  memoryEnabled: boolean;
}

export const DEFAULT_CONCIERGE_SETTINGS: ConciergeSettings = {
  memoryEnabled: true,
};
